import { useMutation } from '@apollo/react-hooks'
import { Box, Grid, Typography } from '@material-ui/core'
import { FieldArray, Form, Formik, FormikProps } from 'formik'
import React from 'react'
import { initialEducationValues } from '.'
import createOrUpdateAccomplishmentsMutation from '../../../apolloClient/mutations/createOrUpdateAccomplishments'
import createOrUpdateEducationMutation from '../../../apolloClient/mutations/createOrUpdateEducations'
import deleteEducationMutation from '../../../apolloClient/mutations/deleteEducation'
import EDUCATION_QUERY from '../../../apolloClient/queries/getEducation'
import ACCOMPLISHMENTS_QUERY from '../../../apolloClient/queries/getAccomplishments'
import { useAuth0 } from '../../../contexts/Auth0Context'
import { useOnboarding } from '../../../contexts/OnboardingContext'
import { DEGREE_TYPES, MIDDLE_OF_CALENDAR_YEAR, getPastNYears } from '../../../shared/constants/educationLevel'
import FormActions from '../../../_global/FormActions'
import ValidatedDropdown from '../../../_global/ValidatedDropdown'
import ValidatedTextField from '../../../_global/ValidatedTextField'
import ActionButtons from '../_local/ActionButtons/index'
import AccomplishmentTags from '../_local/Tags/AccomplishmentTags'
import { validationSchema } from './validationSchema'

interface EducationFormFields {
  educations: [
    {
      id: string
      degree: string
      endDate: any
      major: string
      schoolName: string
      startDate: any
      __typename?: string
      isSaveAndExit?: boolean
    },
  ]
}

type EducationFormProps = {
  initialValues?: any
  profileReviewDialog?: boolean
}

interface PublicationFormFields {
  certificates: [string]
  languages: [string]
}

const setDateToMiddleOfYear = (date: string | number) => {
  return new Date(`${date}-${MIDDLE_OF_CALENDAR_YEAR}`).toLocaleDateString()
}

const EducationDegreeForm = (props: EducationFormProps) => {
  const { logout } = useAuth0()
  const { back, next: nextStep, step, formRef, currentExpert } = useOnboarding()
  const [educationMutation] = useMutation(createOrUpdateEducationMutation)
  const [publicationsMutation] = useMutation(createOrUpdateAccomplishmentsMutation)
  const [deleteEducation] = useMutation(deleteEducationMutation)

  const { initialValues, profileReviewDialog } = props
  const yearItems = getPastNYears(100)

  const buildEducationsMutationInput = (values: EducationFormFields) => {
    const input = values.educations.map(({ __typename, isSaveAndExit, ...educationValues }) => {
      return {
        ...(educationValues.id && { id: educationValues.id }),
        ...educationValues,
        startDate: !educationValues.startDate ? null : setDateToMiddleOfYear(educationValues.startDate),
        endDate: !educationValues.endDate ? null : setDateToMiddleOfYear(educationValues.endDate),
      }
    })

    return {
      expertId: currentExpert.id,
      input,
    }
  }

  const buildPublicationsMutationInput = ({ ...values }: PublicationFormFields) => {
    if (values.languages.length || values.certificates.length) {
      return {
        expertId: currentExpert.id,
        input: {
          certificates: values.certificates,
          languages: values.languages,
        },
      }
    }
    return false
  }

  const handleSubmit = async (values) => {
    const educationPromise = educationMutation({
      variables: buildEducationsMutationInput(values),
      refetchQueries: [{ query: EDUCATION_QUERY, variables: { id: currentExpert.id } }],
    })

    const publicationsValues = buildPublicationsMutationInput(values)
    let publicationsPromise
    if (publicationsValues) {
      publicationsPromise = publicationsMutation({
        variables: publicationsValues,
        refetchQueries: [{ query: ACCOMPLISHMENTS_QUERY, variables: { id: currentExpert.id } }],
      })
    }

    Promise.all([educationPromise, publicationsPromise]).then(() => {
      if (values.isSaveAndExit) {
        logout()
      } else {
        if (step !== 7) {
          nextStep()
        }
      }
    })
  }

  return (
    <Formik
      validateOnMount={true}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      innerRef={formRef}
    >
      {(props: FormikProps<any>) => {
        const { isSubmitting, isValid, setFieldValue, values } = props

        return (
          <Form autoComplete="off">
            <FieldArray name="educations">
              {(arrayHelpers) => (
                <>
                  {values.educations.map((education, index) => {
                    return (
                      <React.Fragment key={index}>
                        <Box my={4}>
                          <Typography variant="body2" gutterBottom>
                            Degree Details
                          </Typography>
                          <ValidatedTextField
                            label="School Name"
                            name={`educations.${index}.schoolName`}
                            type="text"
                            key={index}
                            style={{ width: '75%' }}
                          />

                          <Box mt={2} mb={1}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <ValidatedDropdown
                                  inputLabel={!education.startDate ? undefined : 'Year Enrolled'}
                                  label="Year Enrolled"
                                  name={`educations.${index}.startDate`}
                                  key={`educations.${index}.startDate`}
                                  value={education.startDate || ''}
                                  items={yearItems}
                                  placeholder="Year Enrolled"
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <ValidatedDropdown
                                  inputLabel={!education.endDate ? undefined : 'Year Graduated'}
                                  label="Year Graduated"
                                  name={`educations.${index}.endDate`}
                                  key={`educations.${index}.endDate`}
                                  value={education.endDate || ''}
                                  items={yearItems}
                                  placeholder="Year Graduated"
                                />
                              </Grid>
                            </Grid>
                          </Box>

                          <ValidatedTextField label="Major" name={`educations.${index}.major`} type="text" />
                        </Box>
                        <Typography variant="body2" gutterBottom>
                          Choose your degree type below
                        </Typography>
                        <ValidatedDropdown
                          name={`educations.${index}.degree`}
                          items={DEGREE_TYPES}
                          value={education.degree || ''}
                          placeholder="Degree Type"
                        />

                        {`educations.${index}.degree` === 'other' && (
                          <ValidatedTextField
                            label={'Please comment'}
                            type="text"
                            name={`educations.${index}.degree`}
                            style={{ marginBottom: 35 }}
                          />
                        )}

                        {!profileReviewDialog && (
                          <Box mt={3}>
                            <ActionButtons
                              arrayHelpers={arrayHelpers}
                              index={index}
                              initialValues={initialEducationValues}
                              showButton={values.educations.length > 1}
                              primaryText="Add another degree"
                              secondaryText="Remove degree"
                              deleteRecordMutation={deleteEducation}
                              refetchQuery={EDUCATION_QUERY}
                              values={values.educations}
                              expertId={currentExpert.id}
                            />
                          </Box>
                        )}
                      </React.Fragment>
                    )
                  })}
                </>
              )}
            </FieldArray>
            <Box my={2}>
              <Typography variant="body2" paragraph>
                Certifications and Licenses
              </Typography>
              <AccomplishmentTags
                label="Search Tags"
                setFieldValue={setFieldValue}
                onboardingFormName={'certificates'}
              />
            </Box>
            <Typography variant="body2" paragraph>
              Any languages?
            </Typography>
            <AccomplishmentTags label="Language Tags" setFieldValue={setFieldValue} onboardingFormName={'languages'} />
            {step !== 7 && <FormActions step={step} back={back} isValid={isValid} isSubmitting={isSubmitting} />}
          </Form>
        )
      }}
    </Formik>
  )
}

export default EducationDegreeForm
